/**
 * Servicio para obtener datos desde mindicador.cl
 * API oficial para indicadores económicos de Chile
 */

const MINDICADOR_BASE_URL = 'https://mindicador.cl/api';

/**
 * Servicio para obtener dólar observado desde mindicador.cl
 */
class MindicadorService {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.cacheValidMinutes = 30; // Cache válido por 30 minutos
  }

  /**
   * Obtiene el dólar observado actual
   */
  async getDolarObservado() {
    try {
      // Verificar cache primero
      const cached = this.getCachedValue('dolar');
      if (cached) {
        //console.log('💰 Usando dólar observado desde cache:', cached);
        return cached;
      }

      // Obtener desde API
      //console.log('🌐 Obteniendo dólar observado desde mindicador.cl...');
      const response = await fetch(`${MINDICADOR_BASE_URL}/dolar`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // La API devuelve un array de valores históricos, tomar el más reciente
      if (data.serie && data.serie.length > 0) {
        const ultimoValor = data.serie[0]; // El primer elemento es el más reciente
        const valor = parseFloat(ultimoValor.valor);
        
        if (isNaN(valor) || valor <= 0) {
          throw new Error('Valor de dólar inválido recibido');
        }

        // Redondear a pesos enteros
        const valorRedondeado = Math.round(valor);

        // Guardar en cache el valor redondeado
        this.setCachedValue('dolar', valorRedondeado);
        
        console.log('✅ Dólar observado obtenido:', {
          valorOriginal: valor,
          valorRedondeado,
          fecha: ultimoValor.fecha,
          fuente: 'mindicador.cl'
        });
        
        return valorRedondeado;
      } else {
        throw new Error('No se encontraron datos de dólar en la respuesta');
      }
    } catch (error) {
      console.error('❌ Error obteniendo dólar observado:', error);
      throw error;
    }
  }

  /**
   * Obtiene múltiples indicadores (dólar, UF, etc.)
   */
  async getTodosLosIndicadores() {
    try {
      const response = await fetch(`${MINDICADOR_BASE_URL}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      //console.log('📊 Indicadores obtenidos:', Object.keys(data));
      
      return data;
    } catch (error) {
      console.error('❌ Error obteniendo indicadores:', error);
      throw error;
    }
  }

  /**
   * Obtiene valor desde cache si es válido
   */
  getCachedValue(key) {
    try {
      const cached = this.cache.get(key);
      const expiry = this.cacheExpiry.get(key);
      
      if (cached && expiry && Date.now() < expiry) {
        return cached;
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error leyendo cache:', error);
      return null;
    }
  }

  /**
   * Guarda valor en cache
   */
  setCachedValue(key, value) {
    try {
      const expiry = Date.now() + (this.cacheValidMinutes * 60 * 1000);
      this.cache.set(key, value);
      this.cacheExpiry.set(key, expiry);
    } catch (error) {
      console.error('❌ Error guardando en cache:', error);
    }
  }

  /**
   * Limpia el cache
   */
  clearCache() {
    this.cache.clear();
    this.cacheExpiry.clear();
    //console.log('🗑️ Cache de mindicador limpiado');
  }

  /**
   * Obtiene información de la última actualización
   */
  async getUltimaActualizacion() {
    try {
      const data = await this.getDolarObservado();
      // Podrías agregar lógica para obtener la fecha de la última actualización
      return {
        valor: data,
        timestamp: new Date().toISOString(),
        fuente: 'mindicador.cl'
      };
    } catch (error) {
      console.error('❌ Error obteniendo última actualización:', error);
      throw error;
    }
  }
}

// Exportar instancia singleton
export const mindicadorService = new MindicadorService();